/**
 * 로깅 유틸리티
 *
 * 개발 환경에서는 콘솔 로그를 출력하고,
 * 프로덕션 환경에서는 Sentry로 에러/경고를 전송합니다.
 *
 * @example
 * import { logger } from '@/utils/logger';
 * logger.info('주문 생성 성공:', order);
 * logger.error('결제 실패:', error);
 */

import * as Sentry from '@sentry/react';
import { isErrorValue, safeErrorMetadata } from './safeErrorMetadata';

/**
 * 개발 환경 여부
 * Vite의 import.meta.env.DEV는 빌드 타임에 평가되어 프로덕션에서 코드가 제거됨
 */
const isDev = import.meta.env.DEV;

/**
 * Sentry DSN이 설정되어 있는지 확인
 */
const isSentryEnabled = !!import.meta.env.VITE_SENTRY_DSN;

// Static operation markers remain searchable without changing issue grouping.
const DIAGNOSTIC_MARKERS = new Set([
  'menu.auth.profile-restore.v1',
  'menu.notifications.unread.v1',
  'menu.api.locale-mismatch.v1',
  'menu.orders.active-status.v1',
  'menu.waitlist.bootstrap.v1',
  'menu.waitlist.intake.v1',
  'menu.waitlist.party-read.v1',
  'menu.waitlist.join.v1',
  'menu.waitlist.arrive.v1',
  'menu.waitlist.leave.v1',
  'menu.waitlist.geolocation.v1',
]);

function diagnosticTags(args: unknown[]): { tags?: { diagnostic_marker: string } } {
  for (const arg of args) {
    if (!arg || typeof arg !== 'object' || isErrorValue(arg)) continue;
    try {
      const marker = (arg as Record<string, unknown>).diagnostic_marker;
      if (typeof marker === 'string' && DIAGNOSTIC_MARKERS.has(marker)) {
        return { tags: { diagnostic_marker: marker } };
      }
    } catch { /* Diagnostics must not interrupt the original failure handling. */ }
  }
  return {};
}

/**
 * Error 객체 추출 헬퍼 함수
 * @param args - 로그 인자들
 * @returns 첫 번째 Error 객체 또는 undefined
 */
function extractError(args: unknown[]): Error | undefined {
  for (const arg of args) {
    if (isErrorValue(arg)) {
      return arg;
    }
  }
  return undefined;
}

/**
 * 컨텍스트 추출 헬퍼 함수 (Error가 아닌 인자들)
 * @param args - 로그 인자들
 * @returns 컨텍스트 객체
 */
function extractContext(args: unknown[]): Record<string, unknown> {
  const context: Record<string, unknown> = {};
  args.forEach((arg, index) => {
    if (!isErrorValue(arg)) {
      context[`arg${index}`] = arg;
    }
  });
  return context;
}

/**
 * 로깅 유틸리티 객체
 */
export const logger = {
  /**
   * 정보 로그 (개발 환경에서만 출력)
   * @param message - 로그 메시지
   * @param args - 추가 인자
   */
  info: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.log(`[INFO] ${message}`, ...args);
    }
  },

  /**
   * 경고 로그 (개발: 콘솔 출력, 프로덕션: Sentry에 경고 메시지 추가)
   * @param message - 로그 메시지
   * @param args - 추가 인자
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.warn(`[WARN] ${message}`, ...args);
    }

    // 프로덕션에서 Sentry에 경고 메시지 기록
    if (!isDev && isSentryEnabled) {
      const error = safeErrorMetadata(extractError(args));
      Sentry.captureMessage(message, {
        level: 'warning',
        ...diagnosticTags(args),
        extra: { ...extractContext(args), ...(error ? { error } : {}) },
      });
    }
  },

  /**
   * 에러 로그 (개발: 콘솔 출력, 프로덕션: Sentry로 전송)
   * @param message - 로그 메시지
   * @param args - 추가 인자 (Error 객체 포함 가능)
   */
  error: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.error(`[ERROR] ${message}`, ...args);
    }

    // 프로덕션에서 Sentry로 에러 전송
    if (!isDev && isSentryEnabled) {
      const error = extractError(args);
      const context = extractContext(args);

      if (error instanceof Error) {
        Sentry.captureException(error, {
          ...diagnosticTags(args),
          extra: { message, ...context },
        });
      } else {
        // Error 객체가 없으면 메시지로 캡처
        Sentry.captureMessage(message, {
          level: 'error',
          ...diagnosticTags(args),
          extra: { ...context, ...(error ? { error: safeErrorMetadata(error) } : {}) },
        });
      }
    }
  },

  /**
   * 디버그 로그 (개발 환경에서만 출력)
   * @param message - 로그 메시지
   * @param args - 추가 인자
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (isDev) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  },
};
